/**
 * Shared control-point derivation for tactical line graphics.
 *
 * THIS FILE IS THE SOURCE OF TRUTH. It is copied verbatim into each client:
 *
 *   ft-client-react/src/components/map/tactical/shared/attackLine.js
 *   ft-android/app/src/main/assets/map/js/attackLine.js
 *
 * (Same arrangement as the CoT schema in /proto/ — one authored copy in the
 * umbrella repo, mechanically copied into the clients that need it.)
 *
 * WHY THIS IS SHARED RATHER THAN PORTED
 * ------------------------------------
 * A persisted graphic must render identically on every device. The stored shape
 * is only a centreline (`points`) plus named ground distances (`params`); the
 * renderer needs those turned into the control-point array its draw rule
 * expects. If each client re-derived that transformation, the same arrow would
 * drift between web and Android. So the transformation lives here, once, as a
 * dependency-free pure function, pinned across languages by testvectors.json.
 *
 * Deliberately free of Leaflet, of any projection library, and of any map
 * object: the math is done in a local east/north metre frame, so the result
 * depends only on the inputs — not on the current zoom, screen size, or which
 * client is asking.
 *
 * Coordinates are `[lng, lat]` (GeoJSON axis order) everywhere, matching the
 * `graphics.points` column, the CoT `points` attribute and the renderer's own
 * control-point string.
 */

/* eslint-disable strict -- see the wrapper note below: this file is also loaded
   as a classic <script> in the Android WebView, where the 'use strict'
   directive is meaningful. A bundler sees a module and calls it redundant. */

/*
 * Module wrapper. This file has exactly two consumers, so it detects them
 * directly rather than doing the usual UMD global dance:
 *
 *   - the web client, where a bundler sees `module.exports`;
 *   - the Android WebView, where it loads as a classic <script> and needs a
 *     global.
 *
 * Deliberately no `self`/`this` root detection: Create React App's ESLint
 * config bans bare `self` (`no-restricted-globals`) and treats it as a build
 * error, and `window` is all either consumer actually needs.
 */
(function (factory) {
    var api = factory();
    if (typeof module === 'object' && module.exports) {
        module.exports = api;
    } else if (typeof window !== 'undefined') {
        window.FTAttackLine = api;
    }
}(function () {
    'use strict';

    /** WGS84 semi-major axis, the sphere Web Mercator is built on. */
    var EARTH_RADIUS_M = 6378137;

    /** Metres per degree of latitude on that sphere: pi * R / 180. */
    var M_PER_DEG_LAT = Math.PI * EARTH_RADIUS_M / 180;

    /**
     * Default arrow half-width and arrowhead length, in SCREEN PIXELS.
     *
     * These are *authoring* defaults only: a freshly drawn arrow starts out a
     * sensible size on screen whatever the zoom. A client converts them to
     * ground metres via metersPerPixel() at the moment the graphic is finalised,
     * and it is the metres that get stored and shared. Nothing downstream ever
     * sees pixels.
     */
    var DEFAULT_WIDTH_PX = 60;
    var DEFAULT_HEAD_LENGTH_PX = 70;

    /**
     * Fraction of the final segment the arrowhead may occupy.
     *
     * Clamped so a long head on a short arrow cannot sprawl back past the tail
     * and turn the whole graphic into one giant arrowhead.
     */
    var MAX_HEAD_FRACTION = 0.8;

    /**
     * Web Mercator ground resolution — metres of ground per screen pixel at a
     * given latitude and zoom. Used to convert the pixel authoring defaults into
     * the ground distances that actually get stored.
     */
    function metersPerPixel(lat, zoom) {
        return (2 * Math.PI * EARTH_RADIUS_M * Math.cos(lat * Math.PI / 180)) /
            Math.pow(2, zoom + 8);
    }

    /** Metres per degree of longitude at a given latitude. */
    function metersPerDegLon(lat) {
        return M_PER_DEG_LAT * Math.cos(lat * Math.PI / 180);
    }

    /**
     * Derive the control points for an "Axis of Advance – Main Attack" arrow.
     *
     * The renderer's AXIS draw rule wants: point 1 is the arrowhead TIP, points
     * 2..N-1 continue the centreline with point N-1 as the REAR (tail), and the
     * LAST point is "the back of the arrowhead" — read as a vector from the tip
     * in the arrow's own axis, whose PERPENDICULAR component sets the width and
     * whose ALONG-AXIS (backward) component sets the head length.
     *
     * The user draws tail -> tip, so the centreline is reversed and a synthesised
     * width point appended: [tip, ..., tail, widthPoint].
     *
     * The width point is anchored at the TIP, not the tail: the renderer measures
     * the head against the tip end, so a tail-anchored point turns the entire
     * arrow into one arrowhead.
     *
     * @param {Array<Array<number>>} points  Centreline as [lng, lat], tail -> tip.
     * @param {{width_m: number, head_length_m: number}} params  Ground distances
     *        in metres. Callers with no stored params convert the pixel defaults
     *        via metersPerPixel() first — this function is metres-only by design,
     *        so it never needs to know the zoom.
     * @returns {Array<Array<number>>|null} Control points as [lng, lat], or null
     *          when the geometry is too short to render.
     */
    function controlPoints(points, params) {
        if (!points || points.length < 2) return null;

        var widthM = params && params.width_m;
        var headM = params && params.head_length_m;
        if (!isFinite(widthM) || !isFinite(headM)) return null;

        var reversed = points.slice().reverse();
        var widthPoint = widthControlPoint(points, widthM, headM);
        if (!widthPoint) return null;

        return reversed.concat([widthPoint]);
    }

    /**
     * Synthesise the width control point in a local east/north metre frame
     * centred on the tip.
     *
     * Working in metres rather than projected pixels is the whole point: the
     * result is a property of the geometry, identical on every device, instead of
     * a property of whoever happened to draw it and at what zoom.
     */
    function widthControlPoint(points, widthM, headM) {
        var tip = points[points.length - 1];
        var prev = points[points.length - 2];

        var lon0 = tip[0];
        var lat0 = tip[1];
        var mPerLon = metersPerDegLon(lat0);
        if (mPerLon === 0) return null; // degenerate: at a pole

        // Axis vector, prev -> tip, in metres east/north.
        var dx = (tip[0] - prev[0]) * mPerLon;
        var dy = (tip[1] - prev[1]) * M_PER_DEG_LAT;
        var segLen = Math.sqrt(dx * dx + dy * dy);
        if (segLen === 0) return null; // duplicate vertices: no direction

        var ux = dx / segLen;
        var uy = dy / segLen;

        // Clamp so the head cannot exceed the arrow it belongs to.
        var head = Math.min(headM, segLen * MAX_HEAD_FRACTION);

        // widthPoint = tip + perpendicular * width - axis * head.
        // The perpendicular is a 90-degree rotation of the axis; which of the two
        // normals is used does not matter, because the AXIS rule reads the
        // perpendicular *distance* and renders the body symmetrically about the
        // centreline.
        var wx = -uy * widthM - ux * head;
        var wy = ux * widthM - uy * head;

        return [lon0 + wx / mPerLon, lat0 + wy / M_PER_DEG_LAT];
    }

    return {
        EARTH_RADIUS_M: EARTH_RADIUS_M,
        M_PER_DEG_LAT: M_PER_DEG_LAT,
        DEFAULT_WIDTH_PX: DEFAULT_WIDTH_PX,
        DEFAULT_HEAD_LENGTH_PX: DEFAULT_HEAD_LENGTH_PX,
        MAX_HEAD_FRACTION: MAX_HEAD_FRACTION,
        metersPerPixel: metersPerPixel,
        metersPerDegLon: metersPerDegLon,
        controlPoints: controlPoints
    };
}));
